import { Modal, Form, DatePicker, Select, message } from "antd";
import { useUpdate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useAssignableAuditors } from "./useAssignableAuditors";

interface WaypointEditModalProps {
  visible: boolean;
  onClose: () => void;
  waypoint: {
    id: number;
    waypoint_date?: string | null;
    auditor?: number | { id: number; name?: string } | null;
  } | null;
  auditors: Array<{ id: number; name: string }>;
  auditCompanyId?: number | null;
  countryId?: number | null;
  isAuditorOnly?: boolean;
  currentUser?: { id: number; name: string } | null;
  onSuccess?: () => void;
}

interface WaypointEditFormValues {
  waypoint_date: dayjs.Dayjs | null;
  auditor: number | null;
}

/** WaypointEditModal — edit `waypoint_date` and `auditor` of an existing
 *  Waypoint record. The underlying GPX file is unchanged. */
export const WaypointEditModal = ({
  visible,
  onClose,
  waypoint,
  auditors,
  auditCompanyId,
  countryId,
  isAuditorOnly = false,
  currentUser,
  onSuccess,
}: WaypointEditModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<WaypointEditFormValues>();
  const { mutate: updateWaypoint } = useUpdate();
  const [submitting, setSubmitting] = useState(false);

  const auditorPicker = useAssignableAuditors({
    visible,
    auditCompanyId,
    countryId,
    isAuditorOnly,
    currentUser,
    fallback: auditors,
  });

  useEffect(() => {
    if (visible && waypoint) {
      const auditorId =
        typeof waypoint.auditor === "object" && waypoint.auditor !== null
          ? waypoint.auditor.id
          : (waypoint.auditor as number | null) ?? null;
      form.setFieldsValue({
        waypoint_date: waypoint.waypoint_date ? dayjs(waypoint.waypoint_date) : null,
        // Type-5 auditors can only re-attribute waypoints to themselves.
        auditor: auditorPicker.locked
          ? auditorPicker.defaultValue ?? null
          : auditorId,
      });
    } else if (!visible) {
      form.resetFields();
    }
  }, [
    visible,
    waypoint,
    form,
    auditorPicker.locked,
    auditorPicker.defaultValue,
  ]);

  const handleSubmit = (values: WaypointEditFormValues) => {
    if (!waypoint) return;
    if (!values.waypoint_date) {
      message.error(t("waypoint.date_required", "La fecha es obligatoria"));
      return;
    }
    if (!values.auditor) {
      message.error(t("waypoint.auditor_required", "Selecciona un auditor"));
      return;
    }
    setSubmitting(true);
    updateWaypoint(
      {
        resource: "waypoints",
        id: waypoint.id,
        values: {
          waypoint_date: dayjs(values.waypoint_date).format("YYYY-MM-DD"),
          auditor: values.auditor,
        },
      },
      {
        onSuccess: () => {
          setSubmitting(false);
          message.success(t("waypoint.update_success", "Waypoints actualizados"));
          onClose();
          onSuccess?.();
        },
        onError: (error: any) => {
          setSubmitting(false);
          const detail =
            error?.originalError?.response?.data?.detail
            || error?.response?.data?.detail
            || error?.message;
          message.error(detail || t("waypoint.update_error", "No se pudo actualizar"));
        },
      },
    );
  };

  return (
    <Modal
      title={t("waypoint.edit", "Editar waypoints")}
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save", "Guardar")}
      cancelText={t("common.cancel", "Cancelar")}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
        <Form.Item
          name="waypoint_date"
          label={t("waypoint.date", "Fecha")}
          rules={[
            {
              required: true,
              message: `${t("waypoint.date", "Fecha")} ${t("common.required", "es obligatorio")}`,
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder={t("waypoint.date_placeholder", "Selecciona la fecha")}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          />
        </Form.Item>

        <Form.Item
          name="auditor"
          label={t("waypoint.auditor", "Auditor")}
          rules={[
            {
              required: true,
              message: `${t("waypoint.auditor", "Auditor")} ${t("common.required", "es obligatorio")}`,
            },
          ]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t("common.select", "Selecciona")}
            loading={auditorPicker.loading}
            disabled={auditorPicker.locked}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            options={auditorPicker.options}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
